import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Car,
  CheckCircle,
  Clock,
  Download,
  Eye,
  Globe,
  History,
  Info,
  MapPin,
  Navigation,
  Plus,
  RotateCcw,
  Search,
  Send,
  Settings,
  Trash2,
  UserX,
  Users,
  XCircle,
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import AutoMarker from "../../assets/Auto.png";
import ElecAutoMarker from "../../assets/ElecAuto.png";
import BlockDriverModal from "../../components/BlockDriverModal";
import HistoryModal from "../../components/HistoryModal";
import { useAuth } from "../../contexts/AuthContext";
import usePersistentFilters from "../../hooks/usePersistentFilters";
import { api } from "../../utils/api";
import { confirmPermanentDelete } from "../../utils/confirmPermanentDelete";
import { getReactLeafletTileLayerProps } from "../../utils/mapConfig";

const makeVehicleMarkerIcon = (iconUrl) =>
  new L.Icon({
    iconUrl,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
    popupAnchor: [0, -20],
  });

const AUTO_ICON = makeVehicleMarkerIcon(AutoMarker);
const ELEC_AUTO_ICON = makeVehicleMarkerIcon(ElecAutoMarker);

const FitBounds = ({ points }) => {
  const map = useMap();

  React.useEffect(() => {
    if (!map || !Array.isArray(points) || points.length === 0) return;
    try {
      const bounds = L.latLngBounds(
        points.map(([lat, lng]) => L.latLng(lat, lng)),
      );
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
      }
    } catch (e) {
      // ignore fit failures
    }
  }, [map, points]);

  return null;
};

const Drivers = () => {
  const { hasPermission, user } = useAuth();

  // Function to check for incomplete driver details
  const getIncompleteDetails = (driver) => {
    const missingFields = [];

    // Required driver fields
    if (!driver.name || driver.name.trim() === "") {
      missingFields.push("Name");
    }
    if (!driver.phone || driver.phone.trim() === "") {
      missingFields.push("Phone Number");
    }
    if (!driver.gender || driver.gender.trim() === "") {
      missingFields.push("Gender");
    }
    if (!driver.dob) {
      missingFields.push("Date of Birth");
    }
    if (!driver.licenseNo || driver.licenseNo.trim() === "") {
      missingFields.push("License Number");
    }

    // Required driver documents
    if (
      !driver.documents?.driverPhoto ||
      driver.documents.driverPhoto.trim() === ""
    ) {
      missingFields.push("Driver Photo");
    }
    if (
      !driver.documents?.drivingLicense ||
      driver.documents.drivingLicense.trim() === ""
    ) {
      missingFields.push("Driving License Document");
    }

    // Required bank details
    if (!driver.bankDetails?.UPIID || driver.bankDetails.UPIID.trim() === "") {
      missingFields.push("UPI ID");
    }

    // Aadhar OR Pan required (at least one)
    const hasAadhar =
      (driver.aadharNo && driver.aadharNo.trim() !== "") ||
      (driver.documents?.aadhar && driver.documents.aadhar.trim() !== "");
    const hasPan =
      (driver.panNo && driver.panNo.trim() !== "") ||
      (driver.documents?.panCard && driver.documents.panCard.trim() !== "");

    if (!hasAadhar && !hasPan) {
      missingFields.push("Aadhar or PAN (at least one required)");
    }

    // Vehicle assignment and documents
    if (!driver.vehicleId) {
      missingFields.push("Vehicle Assignment");
    } else {
      const vehicle = driver.vehicleId;
      if (vehicle && typeof vehicle === "object") {
        // Required vehicle fields
        if (!vehicle.regNo || vehicle.regNo.trim() === "") {
          missingFields.push("Vehicle Registration Number");
        }
        if (
          !vehicle.documents?.vehicleRcDoc ||
          vehicle.documents.vehicleRcDoc.trim() === ""
        ) {
          missingFields.push("Vehicle RC Document");
        }

        // Conditional: If isParivahanFailed is true, check for permit fields
        if (vehicle.isParivahanFailed === true) {
          if (
            !vehicle.documents?.permitDoc ||
            vehicle.documents.permitDoc.trim() === ""
          ) {
            missingFields.push("Permit Document");
          }
          if (
            !vehicle.documents?.insurance ||
            vehicle.documents.insurance.trim() === ""
          ) {
            missingFields.push("Insurance Document");
          }
        }
      } else {
        // If vehicle is not populated, mark as incomplete
        missingFields.push("Vehicle Registration Number");
        missingFields.push("Vehicle RC Document");
      }
    }

    return missingFields;
  };

  const defaultFilters = {
    search: "",
    driverId: "",
    status: "",
    statusForApi: "",
    isApproved: "true",
    isBlocked: "",
    // "" (all) | active | deleted: whether the driver deleted their account
    accountStatus: "",
    isVehicleApproved: "",
    incompleteDetails: "",
    bankVerified: false,
    developerOnly: "Exclude",
    approvedAtDate: "",
    regionalOfficeFilter: "",
    // RIDE | ACTING | "" (all) — what the driver does, separate from their role
    serviceType: "",
    // Available drivers whose location updated within this many minutes ("" = off)
    idleMinutes: "",
    earningsPeriod: "all",
    earningsDate: "",
    page: 1,
    limit: 10,
  };

  const {
    filters,
    handleFilterChange,
    handlePageChange,
    clearFilters,
    updateFilters,
  } = usePersistentFilters(defaultFilters, "drivers-filters");

  // Wrapper for status changes: map special UI options to API params
  const handleStatusSelectChange = (value) => {
    if (value === "notavailable") {
      // Online but not available: driverStatus.status === 'online' && isAvailable === false
      updateFilters({
        ...filters,
        status: "notavailable",
        statusForApi: "online",
        isAvailable: "false",
        page: 1,
      });
    } else if (value === "active") {
      // 'active' means driverStatus.status === 'online' AND isAvailable === true
      updateFilters({
        ...filters,
        status: "active",
        statusForApi: "online",
        isAvailable: "true",
        page: 1,
      });
    } else if (value === "available_online") {
      updateFilters({
        ...filters,
        status: "available_online",
        statusForApi: "online",
        isAvailable: "",
        page: 1,
      });
    } else {
      // Regular selection: clear all helper fields set by special options
      updateFilters({
        ...filters,
        status: value,
        statusForApi: "",
        isAvailable: "",
        inTrip: "",
        page: 1,
      });
    }
  };

  const [showBlockModal, setShowBlockModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyDriver, setHistoryDriver] = useState(null);
  const [showRequestsModal, setShowRequestsModal] = useState(false);
  const [requestTrips, setRequestTrips] = useState([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [requestsDriver, setRequestsDriver] = useState(null);
  const [showMapModal, setShowMapModal] = useState(false);
  const [period, setPeriod] = useState("");
  const [showProfileSettingsModal, setShowProfileSettingsModal] =
    useState(false);
  const [regionalOffices, setRegionalOffices] = useState([]);

  // Fetch regional offices for dropdown
  React.useEffect(() => {
    const fetchRegionalOffices = async () => {
      try {
        const response = await api.get("/regional-offices", {
          params: { limit: 100 },
        });
        setRegionalOffices(response?.data?.data?.offices || []);
      } catch (error) {
        console.error("Failed to fetch regional offices:", error);
      }
    };
    fetchRegionalOffices();
  }, []);

  // Mandatory fields based on PendingDrivers.js logic
  const mandatoryFields = [
    "name",
    "phone",
    "gender",
    "dob",
    "licenseNo",
    "driverPhoto",
    "drivingLicense",
    "upiId",
    "aadharOrPan",
    "vehicleAssignment",
    "vehicleRegNo",
    "vehicleRcDoc",
  ];

  const [selectedProfileFields, setSelectedProfileFields] =
    useState(mandatoryFields);

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    ["drivers", filters],
    async () => {
      const params = { ...filters };
      // preserve the UI-level status so server can apply UI semantics (e.g. 'active')
      params.uiStatus = params.status;
      // If UI selected the special options, map to API params
      if (
        params.status === "notavailable" ||
        params.status === "active" ||
        params.status === "available_online"
      ) {
        params.status = params.statusForApi || "online";
      }
      // Remove internal-only helper before sending
      delete params.statusForApi;

      // If the search looks like a 24-character hex ObjectId, use it as driverId
      if (params.search && /^[0-9a-fA-F]{24}$/.test(params.search.trim())) {
        params.driverId = params.search.trim();
      }
      if (typeof params.bankVerified !== "undefined") {
        // API expects string 'true' when filtering for bank verified drivers
        if (params.bankVerified === true) {
          params.isBankVerified = "true";
        }
        delete params.bankVerified;
      }

      const response = await api.get("/drivers", { params });
      return response;
    },
    {
      keepPreviousData: true,
      staleTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: false,
    },
  );

  const {
    data: locationsData,
    isFetching: isFetchingLocations,
    refetch: refetchLocations,
  } = useQuery(
    ["drivers-location", filters],
    async () => {
      const params = { ...filters };
      // for location map we always fetch all matching drivers
      delete params.page;
      delete params.limit;

      // preserve the UI-level status so server can apply UI semantics (e.g. 'active')
      params.uiStatus = params.status;
      if (
        params.status === "notavailable" ||
        params.status === "active" ||
        params.status === "available_online"
      ) {
        params.status = params.statusForApi || "online";
      }
      delete params.statusForApi;

      // If the search looks like a 24-character hex ObjectId, use it as driverId
      if (params.search && /^[0-9a-fA-F]{24}$/.test(params.search.trim())) {
        params.driverId = params.search.trim();
      }

      if (typeof params.bankVerified !== "undefined") {
        if (params.bankVerified === true) {
          params.isBankVerified = "true";
        }
        delete params.bankVerified;
      }

      const response = await api.get("/drivers/location", { params });
      return response;
    },
    {
      enabled: showMapModal,
      staleTime: 0,
      refetchOnWindowFocus: false,
    },
  );

  const refreshDriverLocations = async () => {
    try {
      toast.loading("Refreshing driver locations...", {
        id: "refresh-driver-locations",
      });
      await refetchLocations();
      toast.success("Driver locations refreshed", {
        id: "refresh-driver-locations",
      });
    } catch (e) {
      toast.error("Failed to refresh driver locations", {
        id: "refresh-driver-locations",
      });
    }
  };

  const handleApproveDriver = async (driverId) => {
    try {
      const endpoint = (user?.role === 'ADMIN' || user?.role === 'admin')
        ? `/drivers/admin/${driverId}/approve`
        : `/drivers/${driverId}/approve`;
      await api.put(endpoint);
      toast.success("Driver approved successfully");
      refetch();
    } catch (error) {
      toast.error("Failed to approve driver");
    }
  };

  const handleBlockDriver = async (blockData) => {
    try {
      await api.put(`/drivers/${blockData.driverId}/block`, blockData);
      toast.success(
        `Driver ${blockData.isBlocked ? "blocked" : "unblocked"} successfully`,
      );
      refetch();
    } catch (error) {
      toast.error("Failed to update driver status");
    }
  };

  const getFilterClasses = (filterValue, isCheckbox = false, defaultValue = "") => {
    // Default inactive states: empty string, 'Exclude', 'all', or matching defaultValue
    const isActive = isCheckbox
      ? filterValue === true && defaultValue !== true
      : filterValue &&
      filterValue !== "" &&
      filterValue !== "Exclude" &&
      filterValue !== "all" &&
      filterValue !== defaultValue;
    const baseClasses = isCheckbox
      ? "h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
      : "input";

    if (isActive) {
      return isCheckbox
        ? `${baseClasses} ring-2 ring-blue-400 bg-blue-50`
        : `${baseClasses} border-blue-500 bg-blue-50 shadow-md ring-1 ring-blue-200`;
    }

    return baseClasses;
  };

  // Export functionality
  const handleExportToExcel = async () => {
    try {
      toast.loading("Preparing export...", { id: "export" });

      // Get all filtered data for export (remove pagination limit but keep filters)
      const exportParams = {
        ...filters,
        limit: 10000, // Get all filtered records
        page: 1,
      };

      // Apply same param transformations as the main query
      // preserve the UI-level status so server can apply UI semantics (e.g. 'active')
      exportParams.uiStatus = exportParams.status;
      // If UI selected the special options, map to API params
      if (
        exportParams.status === "notavailable" ||
        exportParams.status === "active" ||
        exportParams.status === "available_online"
      ) {
        exportParams.status = exportParams.statusForApi || "online";
      }
      // Remove internal-only helpers before sending
      delete exportParams.statusForApi;
      delete exportParams.inTrip;

      // If the search looks like a 24-character hex ObjectId, use it as driverId
      if (
        exportParams.search &&
        /^[0-9a-fA-F]{24}$/.test(exportParams.search.trim())
      ) {
        exportParams.driverId = exportParams.search.trim();
      }

      // Map bankVerified to isBankVerified for export (only when checked)
      if (typeof exportParams.bankVerified !== "undefined") {
        if (exportParams.bankVerified === true) {
          exportParams.isBankVerified = "true";
        }
        delete exportParams.bankVerified;
      }

      const exportResponse = await api.get("/drivers", {
        params: exportParams,
      });
      const exportData = exportResponse?.data?.data?.drivers || [];

      if (exportData.length === 0) {
        toast.error("No data available for export with current filters", {
          id: "export",
        });
        return;
      }

      toast.loading("Generating Excel file...", { id: "export" });

      // Prepare data for Excel export
      const excelData = exportData.map((driver, index) => {
        return {
          "S.No": index + 1,
          "Driver Name": driver.name || "N/A",
          Phone: driver.phone || "N/A",
          Email: driver.email || "N/A",
          "License No": driver.licenseNo || "N/A",
          "Vehicle Reg No": driver.vehicleId?.regNo || "No Vehicle",
          "Vehicle Type": driver.vehicleId?.type || "N/A",
          "Vehicle Make": driver.vehicleId?.make || "N/A",
          "Vehicle Model": driver.vehicleId?.model || "N/A",
          Status: driver.isBlocked
            ? "Blocked"
            : driver.isApproved
              ? driver.isAvailable
                ? "Online"
                : "Offline"
              : "Pending",
          "Approval Status": driver.isApproved ? "Approved" : "Pending",
          "Vehicle Approved": driver.isVehicleApproved ? "Yes" : "No",
          Rating: driver.ratingData?.currentrating?.toFixed(1) || "0.0",
          "Total Reviews": driver.ratingData?.count || 0,
          "Total Trips": driver.totalTripsAccepted || 0,
          "Rejected Trips": driver.totalTripsRejected || 0,
          "Earnings (₹)": (driver.driverEarnings || 0).toFixed(2),
          "Due Amount (₹)": (driver.driverDue || 0).toFixed(2),
        };
      });

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const colWidths = [
        { wch: 8 }, // S.No
        { wch: 20 }, // Driver Name
        { wch: 15 }, // Phone
        { wch: 25 }, // Email
        { wch: 20 }, // License No
        { wch: 15 }, // Vehicle Reg No
        { wch: 12 }, // Vehicle Type
        { wch: 15 }, // Vehicle Make
        { wch: 15 }, // Vehicle Model
        { wch: 12 }, // Status
        { wch: 15 }, // Approval Status
        { wch: 15 }, // Vehicle Approved
        { wch: 10 }, // Rating
        { wch: 12 }, // Total Reviews
        { wch: 12 }, // Total Trips
        { wch: 15 }, // Rejected Trips
        { wch: 15 }, // Earnings
        { wch: 15 }, // Due Amount
      ];
      worksheet["!cols"] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "Drivers Report");

      // Generate filename with current date and filter info
      const currentDate = new Date().toISOString().split("T")[0];
      let filename = `Drivers_Report_${currentDate}`;

      // Add filter info to filename if filters are applied
      if (
        filters.search ||
        filters.status ||
        filters.isApproved ||
        filters.isBlocked ||
        filters.isVehicleApproved ||
        filters.developerOnly === "Only"
      ) {
        filename += "_filtered";
      }
      filename += ".xlsx";

      // Download the file
      XLSX.writeFile(workbook, filename);

      toast.success(
        `Exported ${excelData.length} filtered records successfully!`,
        { id: "export" },
      );
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export data. Please try again.", { id: "export" });
    }
  };

  const handleViewDriversOnMap = () => {
    // Open a popup modal with the map view using the /drivers/location endpoint
    setShowMapModal(true);
  };

  // A deleted driver is also un-approved and unavailable. Revoking restores the account
  // (and the vehicle the deletion removed), but the driver must be approved again.
  const handleRestoreDriver = async (driver) => {
    if (
      window.confirm(
        `Restore the account of ${driver.name || driver.phone}? They must be approved again before taking trips.`,
      )
    ) {
      try {
        await api.put(`/drivers/${driver._id}/restore-account`);
        toast.success("Driver account restored. Approve the driver to let them take trips.");
        refetch();
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to restore driver account");
      }
    }
  };

  const handleMarkDriverDeleted = async (driver) => {
    if (
      window.confirm(
        `Mark the account of ${driver.name || driver.phone} as deleted? They will be un-approved and unavailable. You can restore it later.`,
      )
    ) {
      try {
        await api.put(`/drivers/${driver._id}/mark-deleted`, {});
        toast.success("Driver account marked as deleted");
        refetch();
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to mark driver account as deleted");
      }
    }
  };

  const handleDeleteDriver = async (driverId) => {
    if (
      confirmPermanentDelete(
        "this driver record and their vehicle",
        'To keep the record, use "Mark as deleted" instead.',
      )
    ) {
      try {
        await api.delete(`/drivers/${driverId}`);
        toast.success("Driver deleted successfully");
        refetch();
      } catch (error) {
        toast.error("Failed to delete driver");
      }
    }
  };

  if (error) {
    toast.error("Failed to load drivers");
  }

  // Apply frontend filtering if needed (currently none)
  const { drivers } = React.useMemo(() => {
    const rawDrivers = data?.data?.data?.drivers || [];
    // Normalize _id to string when API returns BSON-style objects like {_id: {$oid: '...'}}
    const normalizedDrivers = rawDrivers.map((d) => {
      if (!d) return d;
      const id =
        d._id && typeof d._id === "object" && d._id.$oid
          ? d._id.$oid
          : d._id || d.id;
      return { ...d, _id: id };
    });
    let filteredDrivers = normalizedDrivers;
    let fallback = false;

    if (filters.isVehicleApproved !== undefined && filters.isVehicleApproved !== null && filters.isVehicleApproved !== '') {
      const isApprovedVal = filters.isVehicleApproved === 'true';
      filteredDrivers = filteredDrivers.filter((driver) => {
        const hasApprovedVehicle = driver.vehicleId && (driver.vehicleId.isApproved === true || driver.vehicleId.isApproved === 'true');
        return isApprovedVal ? hasApprovedVehicle : !hasApprovedVehicle;
      });
    }

    if (filters.status === "online") {
      filteredDrivers = normalizedDrivers.filter(
        (driver) =>
          !(
            driver.driverStatus?.status?.toLowerCase() === "online" &&
            !driver.isAvailable
          ),
      );
    }

    if (filters.status === "active") {
      const now = Date.now();
      const ONE_HOUR_MS = 60 * 60 * 1000;
      const parseTimestamp = (rawTs) => {
        if (rawTs == null) return null;
        if (typeof rawTs === "number") {
          return rawTs < 1e12 ? rawTs * 1000 : rawTs;
        }
        if (typeof rawTs === "string") {
          const asNum = Number(rawTs);
          if (!Number.isNaN(asNum)) return asNum < 1e12 ? asNum * 1000 : asNum;
          const parsed = Date.parse(rawTs);
          if (!Number.isNaN(parsed)) return parsed;
          return null;
        }
        return null;
      };

      filteredDrivers = normalizedDrivers.filter((driver) => {
        const rawTs = driver.location?.updatedAt;
        const ts = parseTimestamp(rawTs);
        if (!ts) return false;
        return now - ts <= ONE_HOUR_MS;
      });

      // If server returned drivers but none pass the recency filter,
      // fallback to showing the server list and signal fallback.
      if (filteredDrivers.length === 0 && normalizedDrivers.length > 0) {
        filteredDrivers = normalizedDrivers;
        fallback = true;
      }
    }

    return { drivers: filteredDrivers, recencyFallback: fallback };
  }, [data?.data?.data?.drivers, filters.status, filters.isVehicleApproved]);

  const mapDrivers = useMemo(() => {
    const raw = locationsData?.data?.data?.drivers || [];
    return raw.filter((d) => {
      const coords = d?.location?.coordinates;
      return (
        Array.isArray(coords) &&
        coords.length >= 2 &&
        Number.isFinite(Number(coords[0])) &&
        Number.isFinite(Number(coords[1]))
      );
    });
  }, [locationsData]);

  const mapPoints = useMemo(() => {
    return mapDrivers.map((d) => [
      Number(d.location.coordinates[1]),
      Number(d.location.coordinates[0]),
    ]);
  }, [mapDrivers]);

  const getDriverMarkerIcon = (driver) => {
    const vehicle = driver?.vehicleId;
    const type = String(vehicle?.type || "").toUpperCase();
    return type === "ELECTRIC_AUTO" ? ELEC_AUTO_ICON : AUTO_ICON;
  };

  const pagination = data?.data?.data?.pagination || {};

  const getStatusBadge = (driver) => {
    if (driver.isDeleted === true) {
      const when = driver.deletedAt
        ? new Date(driver.deletedAt).toLocaleDateString()
        : null;
      return (
        <div>
          <span className="badge badge-danger">Deleted</span>
          {when && <div className="text-xs text-gray-500 mt-1">{when}</div>}
        </div>
      );
    }
    if (driver.isBlocked) {
      return <span className="badge badge-danger">Blocked</span>;
    }
    if (!driver.isApproved) {
      return <span className="badge badge-warning">Pending</span>;
    }

    // Check if driver is in a trip based on trip status
    if (driver.currentTripId && driver.currentTripStatus) {
      const tripStatus = driver.currentTripStatus;

      // Show "In Trip" for ACCEPTED and PICKEDUP statuses
      if (tripStatus === "ACCEPTED" || tripStatus === "PICKEDUP") {
        const tripStatusLabel =
          tripStatus === "PICKEDUP" ? "Picked Up" : "Accepted";
        const tripStatusBadgeClass =
          tripStatus === "ACCEPTED" ? "badge-primary" : "badge-info";
        return (
          <div className="flex flex-col items-start space-y-1">
            <div className="flex items-center gap-2">
              <span className="badge badge-info">In Trip</span>
              <span className={`badge ${tripStatusBadgeClass}`}>
                {tripStatusLabel}
              </span>
            </div>
            {hasPermission("trips", "view") && (
              <Link
                to={`/trips/${driver.currentTripId}`}
                className="text-xs text-primary-600 hover:text-primary-800 underline"
              >
                View Trip
              </Link>
            )}
          </div>
        );
      }
      // Show "Last Trip" for other completed statuses
      if (
        tripStatus === "DROPPED" ||
        tripStatus === "PAYMENT_COMPLETED" ||
        tripStatus === "COMPLETED"
      ) {
        return (
          <div className="flex flex-col items-start space-y-1">
            {driver.driverStatus?.status?.toLowerCase() === "online" &&
              driver.isAvailable ? (
              <span className="badge badge-success">Online</span>
            ) : driver.driverStatus?.status?.toLowerCase() === "online" &&
              !driver.isAvailable ? (
              <span className="badge badge-warning">
                Online - Not Available
              </span>
            ) : (
              <span className="badge badge-danger">Offline</span>
            )}
            <span className="badge badge-info">Last Trip</span>
            {hasPermission("trips", "view") && (
              <Link
                to={`/trips/${driver.currentTripId}`}
                className="text-xs text-primary-600 hover:text-primary-800 underline"
              >
                View Last Trip
              </Link>
            )}
          </div>
        );
      }
    }

    if (driver.driverStatus?.status?.toLowerCase() === "offline") {
      return <span className="badge badge-info">Offline</span>;
    }
    if (
      driver.driverStatus?.status?.toLowerCase() === "online" &&
      !driver.isAvailable
    ) {
      return (
        <span className="badge badge-warning">Online - Not Available</span>
      );
    }
    return <span className="badge badge-success">Online</span>;
  };

  // "Idle 2h 15m" for online, available drivers whose location has not updated for 30+ minutes
  const getIdleLabel = (driver) => {
    if (driver.driverStatus?.status?.toLowerCase() !== "online" || !driver.isAvailable) return null;
    const updated = driver.location?.updatedAt;
    if (!updated) return null;
    const mins = Math.floor((Date.now() - new Date(updated).getTime()) / 60000);
    if (!Number.isFinite(mins) || mins < 30) return null;
    const label = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
    return <span className="text-xs text-amber-600">Idle {label}</span>;
  };

  // Helpers for trips UI inside requests modal
  const getTripStatusBadge = (trip) => {
    const s = String(trip?.status || "").toUpperCase();
    switch (s) {
      case "COMPLETED":
        return <span className="badge badge-success">Completed</span>;
      case "PICKEDUP":
      case "STARTED":
        return <span className="badge badge-info">{s}</span>;
      case "ACCEPTED":
        return <span className="badge badge-primary">Accepted</span>;
      case "PENDING":
      case "WAITING":
        return <span className="badge badge-warning">Waiting</span>;
      case "CANCELLED":
        return <span className="badge badge-danger">Cancelled</span>;
      default:
        return (
          <span className="badge badge-secondary">
            {trip?.status || "Unknown"}
          </span>
        );
    }
  };

  const getPaymentMethodBadge = (method) => {
    const methodConfig = {
      CASH: { class: "badge-success", text: "Cash" },
      CARD: { class: "badge-primary", text: "Card" },
      UPI: { class: "badge-info", text: "UPI" },
      WALLET: { class: "badge-warning", text: "Wallet" },
      ONLINE: { class: "badge-primary", text: "Online" },
    };

    const key = String(method || "").toUpperCase();
    const config = methodConfig[key] || {
      class: "badge-info",
      text: method || "N/A",
    };
    return <span className={`badge ${config.class}`}>{config.text}</span>;
  };

  const handleViewRequests = async (driver, eventType) => {
    setRequestsDriver(driver);
    setShowRequestsModal(true);
    setIsLoadingRequests(true);
    try {
      const res = await api.get(`/drivers/${driver._id}/requests`, {
        params: { page: 1, limit: 200, eventType },
      });
      const trips = res?.data?.data?.trips || [];
      setRequestTrips(trips || []);
    } catch (err) {
      console.error("Error fetching driver trips", err);
      setRequestTrips([]);
    } finally {
      setIsLoadingRequests(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="page-title">Drivers</h1>
              <p className="page-subtitle">
                Manage driver accounts and approvals
              </p>
            </div>
            {hasPermission("drivers", "write") && (
              <div className="flex items-center space-x-3">
                <Link
                  to="/drivers/driverworkhistory"
                  className="btn btn-outline"
                >
                  <History className="h-4 w-4 mr-2" />
                  Driver Work History
                </Link>
                <Link to="/drivers/pending" className="btn btn-outline">
                  <Clock className="h-4 w-4 mr-2" />
                  Pending Approvals
                </Link>

                <Link to="/drivers/create" className="btn btn-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Driver
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="card-title">Filters</h3>
              {/* {(() => {
                const activeCount = Object.entries(filters).filter(([key, value]) => {
                  if (key === 'page' || key === 'limit') return false;
                  if (typeof value === 'boolean') return value === true;
                  // For developer filter, only count 'Only' as active
                  if (key === 'developerOnly') return value === 'Only';
                  return value && value !== '';
                }).length;

                return activeCount > 0 ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {activeCount} active
                  </span>
                ) : null;
              })()} */}
            </div>
            <button onClick={clearFilters} className="btn btn-outline btn-sm">
              Clear Filters
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label className="form-label">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, phone"
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  className={`${getFilterClasses(filters.search)} pl-10`}
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="form-label">Live Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleStatusSelectChange(e.target.value)}
                className={getFilterClasses(filters.status)}
              >
                <option value="">All</option>
                <option value="online">Online</option>
                <option value="busy">In Trip</option>
                <option value="active">Online & Active</option>
                <option value="available_online">Online & Available</option>
                <option value="notavailable">Online - Not Available</option>
                <option value="offline">Offline</option>
              </select>
            </div>

            {/* Approval Filter */}
            <div>
              <label className="form-label">Approval Status</label>
              <select
                value={filters.isApproved}
                onChange={(e) =>
                  handleFilterChange("isApproved", e.target.value)
                }
                className={getFilterClasses(filters.isApproved, false, "true")}
              >
                <option value="">All</option>
                <option value="true">Approved</option>
                <option value="false">Pending</option>
                <option value="bankApproved">Bank Approved</option>
                <option value="bankNotApproved">Bank Not Approved</option>
              </select>
            </div>

            {/* Vehicle Approved Filter */}
            <div>
              <label className="form-label">Vehicle Approval</label>
              <select
                value={filters.isVehicleApproved}
                onChange={(e) =>
                  handleFilterChange("isVehicleApproved", e.target.value)
                }
                className={getFilterClasses(filters.isVehicleApproved)}
              >
                <option value="">All</option>
                <option value="true">Approved</option>
                <option value="false">Pending</option>
              </select>
            </div>

            {/* Blocked Filter */}
            <div>
              <label className="form-label">Blocked</label>
              <select
                value={filters.isBlocked}
                onChange={(e) =>
                  handleFilterChange("isBlocked", e.target.value)
                }
                className={getFilterClasses(filters.isBlocked)}
              >
                <option value="">All</option>
                <option value="true">Blocked</option>
                <option value="false">Active</option>
              </select>
            </div>

            {/* Account deletion filter. A deleted driver is un-approved, so choosing
                Deleted also clears the "approved only" default that would hide them. */}
            <div>
              <label className="form-label">Account</label>
              <select
                value={filters.accountStatus || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  updateFilters({
                    ...filters,
                    accountStatus: value,
                    ...(value === "deleted" ? { isApproved: "" } : {}),
                    page: 1,
                  });
                }}
                className={getFilterClasses(filters.accountStatus)}
              >
                <option value="">All</option>
                <option value="active">Not Deleted</option>
                <option value="deleted">Deleted</option>
              </select>
            </div>

            {/* Driver Role Filter */}
            <div>
              <label className="form-label">Driver Role</label>
              <select
                value={filters.role}
                onChange={(e) => handleFilterChange("role", e.target.value)}
                className={getFilterClasses(filters.role)}
              >
                <option value="">All Roles</option>
                <option value="dco">DCO</option>
                <option value="salaried">Salaried</option>
                <option value="actingdriver">Acting Driver</option>
              </select>
            </div>

            {/* Service Type Filter — ride / acting, not the role above */}
            <div>
              <label className="form-label">Service Type</label>
              <select
                value={filters.serviceType || ""}
                onChange={(e) => handleFilterChange("serviceType", e.target.value)}
                className={getFilterClasses(filters.serviceType)}
              >
                <option value="">All Services</option>
                <option value="RIDE">Ride</option>
                <option value="ACTING">Acting</option>
              </select>
            </div>

            {/* Last Updated Filter — available drivers (not on a trip) whose location updated within the chosen time */}
            <div>
              <label className="form-label">Location Updated</label>
              <select
                value={filters.idleMinutes || ""}
                onChange={(e) => handleFilterChange("idleMinutes", e.target.value)}
                className={getFilterClasses(filters.idleMinutes)}
              >
                <option value="">Any</option>
                <option value="30">Within 30 minutes</option>
                <option value="60">Within 1 hour</option>
                <option value="120">Within 2 hours</option>
                <option value="360">Within 6 hours</option>
                <option value="720">Within 12 hours</option>
              </select>
            </div>

            {/* Regional Office Filter */}
            <div>
              <label className="form-label">Regional Office</label>
              <select
                value={filters.regionalOfficeFilter}
                onChange={(e) =>
                  handleFilterChange("regionalOfficeFilter", e.target.value)
                }
                className={getFilterClasses(filters.regionalOfficeFilter)}
              >
                <option value="">All Offices</option>
                {regionalOffices.map((office) => (
                  <option key={office._id} value={office._id}>
                    {office.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Profile Completion Filter */}
            <div>
              <label className="form-label">Profile Completion</label>
              <div className="flex items-center gap-2">
                <select
                  value={filters.incompleteDetails}
                  onChange={(e) =>
                    handleFilterChange("incompleteDetails", e.target.value)
                  }
                  className={getFilterClasses(filters.incompleteDetails)}
                >
                  <option value="">All Drivers</option>
                  <option value="complete">
                    Complete Profile & Vehicle Docs
                  </option>
                  <option value="incomplete">
                    Missing Profile or Vehicle Docs
                  </option>
                </select>
                <button
                  onClick={() => {
                    setSelectedProfileFields(mandatoryFields);
                    setShowProfileSettingsModal(true);
                  }}
                  className="btn btn-outline btn-sm p-2"
                  title="Configure Profile Fields"
                >
                  <Settings className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Developer Filter */}
            <div>
              <label className="form-label">Developer Filter</label>
              <select
                value={filters.developerOnly}
                onChange={(e) =>
                  handleFilterChange("developerOnly", e.target.value)
                }
                className={getFilterClasses(filters.developerOnly)}
              >
                <option value="Exclude">Exclude Developers</option>
                <option value="Include">Include Developers</option>
                <option value="Only">Developers Only</option>
              </select>
            </div>

            {/* Bank Verified Filter */}
            <div>
              <label className="form-label">Bank Verified</label>
              <div className="flex items-center mt-2">
                <input
                  type="checkbox"
                  id="bankVerified"
                  checked={filters.bankVerified}
                  onChange={(e) =>
                    handleFilterChange("bankVerified", e.target.checked)
                  }
                  className={getFilterClasses(filters.bankVerified, true)}
                />
                <label
                  htmlFor="bankVerified"
                  className="ml-2 text-sm font-medium text-gray-700"
                >
                  Bank Verified
                </label>
              </div>
            </div>

            {/* Earnings Period Filter */}
            <div>
              <label className="form-label">Earnings Period</label>
              <select
                value={filters.earningsPeriod || "all"}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "custom") {
                    updateFilters({
                      ...filters,
                      earningsPeriod: "custom",
                      page: 1,
                    });
                  } else {
                    updateFilters({
                      ...filters,
                      earningsPeriod: val,
                      earningsDate: "",
                      page: 1,
                    });
                  }
                }}
                className={getFilterClasses(filters.earningsPeriod)}
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="thisMonth">This Month</option>
                <option value="custom">Specific Date</option>
              </select>
            </div>

            {/* Earnings Specific Date */}
            {filters.earningsPeriod === "custom" && (
              <div>
                <label className="form-label">Earnings Specific Date</label>
                <input
                  type="date"
                  value={filters.earningsDate || ""}
                  onChange={(e) =>
                    handleFilterChange("earningsDate", e.target.value)
                  }
                  className={getFilterClasses(filters.earningsDate)}
                />
              </div>
            )}

            {/* Approved At Date */}
            <div>
              <label className="form-label">Approved At</label>
              <input
                type="date"
                value={filters.approvedAtDate || ""}
                onChange={(e) => {
                  setPeriod("");
                  handleFilterChange("approvedAtDate", e.target.value);
                }}
                className={getFilterClasses(filters.approvedAtDate)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Drivers Table */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Drivers ({pagination.totalRecords || 0})
            </h3>
            <div className="flex items-center gap-3">
              <button
                onClick={handleViewDriversOnMap}
                className="btn btn-outline"
                disabled={isLoading || drivers.length === 0}
              >
                <MapPin className="h-4 w-4 mr-2" />
                View on Map
              </button>
              <button
                onClick={handleExportToExcel}
                className="btn btn-outline"
                disabled={isLoading || drivers.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </button>
            </div>
          </div>
        </div>
        <div className="card-body p-0 sm:p-0">
          {isLoading || isFetching ? (
            <div className="flex items-center justify-center py-8">
              <div className="loading-spinner"></div>
            </div>
          ) : drivers.length === 0 ? (
            <div className="empty-state p-6">
              <Users className="empty-state-icon" />
              <h3 className="empty-state-title">No drivers found</h3>
              <p className="empty-state-description">
                No drivers match your current filters.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Driver</th>
                    <th className="table-header-cell">Phone</th>
                    <th className="table-header-cell">Vehicle</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Rating</th>
                    <th className="table-header-cell">Trips</th>
                    <th className="table-header-cell">Earnings</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {drivers.map((driver) => (
                    <tr key={driver._id} className="table-row">
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary-700">
                              {driver.name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 flex items-center">
                              {driver.name}
                              {(() => {
                                const missingFields =
                                  getIncompleteDetails(driver);
                                return missingFields.length > 0 ? (
                                  <div className="relative group ml-2">
                                    <Info className="h-4 w-4 text-orange-500 cursor-help" />
                                    <div className="absolute left-full top-0 ml-2 w-72 bg-white text-gray-800 text-xs rounded-lg shadow-xl border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-normal p-4 z-10">
                                      <div className="font-semibold text-gray-900 mb-2 flex items-center gap-1.5 border-b border-gray-100 pb-1.5">
                                        <Info className="h-4 w-4 text-orange-500" />
                                        <span>Missing Profile Details</span>
                                      </div>
                                      <div className="space-y-1.5 text-left">
                                        {missingFields.map((field, index) => (
                                          <div key={index} className="flex items-start gap-1.5 text-gray-600">
                                            <span className="text-orange-500 mt-0.5">•</span>
                                            <span className="font-medium">{field}</span>
                                          </div>
                                        ))}
                                      </div>
                                      <div className="absolute right-full top-1.5 border-4 border-transparent border-r-white"></div>
                                      <div className="absolute right-full top-1.5 border-4 border-transparent border-r-gray-200 -mr-px -z-10"></div>
                                    </div>
                                  </div>
                                ) : null;
                              })()}
                            </div>
                            <div className="text-sm text-gray-500">
                              {driver.licenseNo}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-900">
                          {driver.phone}
                        </div>
                        <div className="text-sm text-gray-500">
                          {driver.email}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center">
                          <Car className="h-4 w-4 text-gray-400 mr-2" />
                          <div>
                            {driver.vehicleId ? (
                              <>
                                <div className="text-sm text-gray-900">
                                  {driver.vehicleId.regNo || "Vehicle Assigned"}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {driver.vehicleId.model}
                                </div>
                              </>
                            ) : (
                              <div className="text-sm text-gray-500">
                                No Vehicle
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex flex-col items-start space-y-1">
                          {getStatusBadge(driver)}
                          {getIdleLabel(driver)}
                          {driver.location && (
                            <Link
                              to={`/drivers/${driver._id}/location`}
                              className="flex items-center text-xs text-primary-600 hover:text-primary-800"
                            >
                              <MapPin className="h-3 w-3 mr-1" />
                              Track Location
                            </Link>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-gray-900">
                            {driver.ratingData?.currentrating?.toFixed(1) ||
                              "0.0"}
                          </span>
                          <span className="text-sm text-gray-500 ml-1">
                            ({driver.ratingData?.count || 0})
                          </span>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="space-y-1">
                          <div className="text-sm text-gray-900">
                            {driver.totalTripsAccepted || 0} /{" "}
                            {driver.totalTripsRejected || 0}
                          </div>
                          <div className="text-sm text-gray-500">
                            Accepted / Rejected
                          </div>
                          {driver.todayTripStats &&
                            driver.todayTripStats.totalEvents > 0 && (
                              <div className="mt-2 pt-2 border-t border-gray-200">
                                <div className="text-xs font-medium text-gray-700 mb-1">
                                  Today:
                                </div>
                                <div className="flex items-center gap-3">
                                  {/* Requests Sent */}
                                  <div className="relative group">
                                    <button
                                      onClick={() =>
                                        handleViewRequests(
                                          driver,
                                          "REQUEST_SENT",
                                        )
                                      }
                                      className="flex items-center gap-1 cursor-pointer focus:outline-none"
                                    >
                                      <Send className="h-3.5 w-3.5 text-blue-500" />
                                      <span className="text-xs font-medium text-gray-700">
                                        {driver.todayTripStats
                                          .requestAttempts || 0}
                                      </span>
                                    </button>
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-10">
                                      Requests Sent:{" "}
                                      {driver.todayTripStats.requestAttempts ||
                                        0}
                                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                                    </div>
                                  </div>

                                  {/* Accepted */}
                                  <div className="relative group">
                                    <div className="flex items-center gap-1 cursor-help">
                                      <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                                      <span className="text-xs font-medium text-green-600">
                                        {driver.todayTripStats
                                          .requestsAccepted || 0}
                                      </span>
                                    </div>
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-10">
                                      Accepted:{" "}
                                      {driver.todayTripStats.requestsAccepted ||
                                        0}
                                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                                    </div>
                                  </div>

                                  {/* TimeOut */}
                                  <div className="relative group">
                                    <div className="flex items-center gap-1 cursor-help">
                                      <Clock className="h-3.5 w-3.5 text-yellow-500" />
                                      <span className="text-xs font-medium text-yellow-600">
                                        {driver.todayTripStats.timeouts || 0}
                                      </span>
                                    </div>
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-10">
                                      Timeouts:{" "}
                                      {driver.todayTripStats.timeouts || 0}
                                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                                    </div>
                                  </div>

                                  {/* Socket Not connected */}
                                  <div className="relative group">
                                    <div className="flex items-center gap-1 cursor-help">
                                      <Globe className="h-3.5 w-3.5 text-gray-500" />
                                      <span className="text-xs font-medium text-gray-600">
                                        {driver.todayTripStats
                                          .socketNotConnected || 0}
                                      </span>
                                    </div>
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-10">
                                      Socket Not Connected:{" "}
                                      {driver.todayTripStats
                                        .socketNotConnected || 0}
                                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                                    </div>
                                  </div>

                                  {/* Rejected */}
                                  <div className="relative group">
                                    <div className="flex items-center gap-1 cursor-help">
                                      <XCircle className="h-3.5 w-3.5 text-red-500" />
                                      <span className="text-xs font-medium text-red-600">
                                        {driver.todayTripStats
                                          .requestsRejected || 0}
                                      </span>
                                    </div>
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-10">
                                      Rejected:{" "}
                                      {driver.todayTripStats.requestsRejected ||
                                        0}
                                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm font-medium text-gray-900">
                          ₹{driver.driverEarnings?.toFixed(2) || "0.00"}{" "}
                          <span className="text-xs font-normal text-gray-500">
                            ({driver.driverEarningsTripCount || 0}{" "}
                            {driver.driverEarningsTripCount === 1
                              ? "trip"
                              : "trips"})
                          </span>
                        </div>
                        <div className="text-sm text-gray-500">
                          Due: ₹{driver.driverDue?.toFixed(2) || "0.00"}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          <Link
                            to={`/drivers/${driver._id}`}
                            className="text-primary-600 hover:text-primary-900"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Link>

                          <button
                            onClick={() => {
                              setHistoryDriver(driver);
                              setShowHistoryModal(true);
                            }}
                            className="text-secondary-600 hover:text-secondary-900"
                            title="View History"
                          >
                            <History className="h-4 w-4" />
                          </button>

                          {driver.location && (
                            <Link
                              to={`/drivers/${driver._id}/location`}
                              className="text-info-600 hover:text-info-900"
                              title="Track Location"
                            >
                              <Navigation className="h-4 w-4" />
                            </Link>
                          )}

                          {hasPermission("drivers", "write") &&
                            !driver.isApproved &&
                            driver.isDeleted !== true && (
                              <button
                                onClick={() => handleApproveDriver(driver._id)}
                                className="text-success-600 hover:text-success-900"
                                title="Approve Driver"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </button>
                            )}

                          {hasPermission("drivers", "write") && (
                            <button
                              onClick={() => {
                                setSelectedDriver(driver);
                                setShowBlockModal(true);
                              }}
                              className={
                                driver.isBlocked
                                  ? "text-success-600 hover:text-success-900"
                                  : "text-warning-600 hover:text-warning-900"
                              }
                              title={
                                driver.isBlocked
                                  ? "Unblock Driver"
                                  : "Block Driver"
                              }
                            >
                              {driver.isBlocked ? (
                                <CheckCircle className="h-4 w-4" />
                              ) : (
                                <XCircle className="h-4 w-4" />
                              )}
                            </button>
                          )}

                          {hasPermission("drivers", "write") &&
                            driver.isDeleted === true && (
                              <button
                                onClick={() => handleRestoreDriver(driver)}
                                className="text-success-600 hover:text-success-900"
                                title="Restore deleted account"
                              >
                                <RotateCcw className="h-4 w-4" />
                              </button>
                            )}

                          {hasPermission("drivers", "delete") &&
                            driver.isDeleted !== true && (
                              <button
                                onClick={() => handleMarkDriverDeleted(driver)}
                                className="text-warning-600 hover:text-warning-900"
                                title="Mark account as deleted (can be restored)"
                              >
                                <UserX className="h-4 w-4" />
                              </button>
                            )}

                          {hasPermission("drivers", "delete") && (
                            <button
                              onClick={() => handleDeleteDriver(driver._id)}
                              className="text-danger-600 hover:text-danger-900"
                              title="Delete permanently"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination Footer */}
        {(pagination.totalRecords > 0 || drivers.length > 0) && (
          <div className="card-footer">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {(pagination.current - 1) * (pagination.limit || 10) + 1} to{" "}
                {Math.min(
                  (pagination.current || 1) * (pagination.limit || 10),
                  pagination.totalRecords || drivers.length,
                )}{" "}
                of {pagination.totalRecords || drivers.length} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange((pagination.current || 1) - 1)}
                  disabled={pagination.current === 1 || !pagination.current}
                  className="btn btn-outline btn-sm"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange((pagination.current || 1) + 1)}
                  disabled={
                    !pagination.total || pagination.current >= pagination.total
                  }
                  className="btn btn-outline btn-sm"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Block Driver Modal */}
      {selectedDriver && (
        <BlockDriverModal
          isOpen={showBlockModal}
          onClose={() => {
            setShowBlockModal(false);
            setSelectedDriver(null);
          }}
          driver={selectedDriver}
          onBlock={(blockData) => {
            handleBlockDriver({ ...blockData, driverId: selectedDriver._id });
          }}
        />
      )}

      {/* History Modal */}
      {historyDriver && (
        <HistoryModal
          isOpen={showHistoryModal}
          onClose={() => {
            setShowHistoryModal(false);
            setHistoryDriver(null);
          }}
          driverId={historyDriver._id}
          driverName={historyDriver.name}
        />
      )}
      {/* Requests Sent Modal */}
      {showRequestsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-7xl w-full mx-4 relative">
            <button
              onClick={() => {
                setShowRequestsModal(false);
                setRequestTrips([]);
                setRequestsDriver(null);
              }}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>

            <h3 className="text-lg font-semibold mb-4 text-gray-900">
              {requestsDriver?.name || ""}
            </h3>

            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Trip ID</th>
                    <th className="table-header-cell">Passenger</th>
                    <th className="table-header-cell">Driver</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Payment</th>
                    <th className="table-header-cell">Fare</th>
                    <th className="table-header-cell">Created</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {isLoadingRequests ? (
                    <tr>
                      <td colSpan="9" className="py-6 text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : requestTrips.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="py-6 text-center">
                        No trips found
                      </td>
                    </tr>
                  ) : (
                    requestTrips.map((trip) => {
                      const bookingForName = trip?.bookingForName || "--";
                      const driverName =
                        trip?.driverId?.name ||
                        trip?.driverId?.phone ||
                        trip?.driverName ||
                        "Driver";
                      const fare =
                        trip?.minFare ?? trip?.fare ?? trip?.totalFare ?? 0;

                      return (
                        <tr key={trip._id} className="table-row">
                          <td className="table-cell">
                            <Link
                              to={`/trips/${trip._id}`}
                              className="text-primary-600 hover:underline"
                            >
                              {trip._id}
                            </Link>
                          </td>
                          <td className="table-cell text-sm text-gray-900">
                            {bookingForName}
                          </td>
                          <td className="table-cell text-sm text-gray-900">
                            {driverName}
                          </td>
                          <td className="table-cell">
                            {getTripStatusBadge(trip)}
                          </td>
                          <td className="table-cell">
                            {getPaymentMethodBadge(
                              trip.paymentMethod ||
                              trip.payment?.method ||
                              trip.payment?.paymentMethod,
                            )}
                          </td>
                          <td className="table-cell text-sm text-gray-900">
                            ₹{Number(fare).toFixed(2)}
                          </td>
                          <td className="table-cell text-sm text-gray-500">
                            {trip.bookingTime
                              ? new Date(trip.bookingTime).toLocaleDateString()
                              : trip.createdAt
                                ? new Date(trip.createdAt).toLocaleString()
                                : ""}
                          </td>
                          <td className="table-cell">
                            <Link
                              to={`/trips/${trip._id}`}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              View
                            </Link>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowRequestsModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Settings Modal */}
      {showProfileSettingsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 relative">
            <button
              onClick={() => setShowProfileSettingsModal(false)}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              ✕
            </button>

            <h3 className="text-lg font-semibold mb-4 text-gray-900">
              Profile Completion Fields
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Select the fields to filter drivers who have completed these
              specific fields
            </p>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {[
                { id: "name", label: "Driver Name", mandatory: true },
                { id: "phone", label: "Phone Number", mandatory: true },
                { id: "gender", label: "Gender", mandatory: true },
                { id: "dob", label: "Date of Birth", mandatory: true },
                { id: "licenseNo", label: "License Number", mandatory: true },
                { id: "driverPhoto", label: "Driver Photo", mandatory: true },
                {
                  id: "drivingLicense",
                  label: "Driving License Document",
                  mandatory: true,
                },
                { id: "upiId", label: "UPI ID", mandatory: true },
                {
                  id: "aadharOrPan",
                  label: "Aadhar or PAN (at least one required)",
                  mandatory: true,
                },
                {
                  id: "vehicleAssignment",
                  label: "Vehicle Assignment",
                  mandatory: true,
                },
                {
                  id: "vehicleRegNo",
                  label: "Vehicle Registration Number",
                  mandatory: true,
                },
                {
                  id: "vehicleRcDoc",
                  label: "Vehicle RC Document",
                  mandatory: true,
                },
                {
                  id: "permitDoc",
                  label: "Permit Document (if Parivahan failed)",
                  mandatory: false,
                },
                {
                  id: "insuranceDoc",
                  label: "Insurance Document (if Parivahan failed)",
                  mandatory: false,
                },
              ].map((field) => (
                <div key={field.id} className="flex items-center">
                  <input
                    type="checkbox"
                    id={field.id}
                    checked={selectedProfileFields.includes(field.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedProfileFields([
                          ...selectedProfileFields,
                          field.id,
                        ]);
                      } else {
                        setSelectedProfileFields(
                          selectedProfileFields.filter((f) => f !== field.id),
                        );
                      }
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor={field.id}
                    className="ml-2 text-sm font-medium text-gray-700"
                  >
                    {field.label}
                    {field.mandatory && (
                      <span className="ml-1 text-red-500">*</span>
                    )}
                  </label>
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setSelectedProfileFields([]);
                  setShowProfileSettingsModal(false);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (selectedProfileFields.length > 0) {
                    updateFilters({
                      ...filters,
                      profileFields: selectedProfileFields.join(","),
                      incompleteDetails: "custom",
                      page: 1,
                    });
                  }
                  setShowProfileSettingsModal(false);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                disabled={selectedProfileFields.length === 0}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drivers on Map Modal */}
      {showMapModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-7xl w-full mx-4 relative">
            <button
              onClick={() => setShowMapModal(false)}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              ✕
            </button>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Drivers on Map
                  </h3>
                  <p className="text-sm text-gray-600">
                    Showing {mapDrivers.length} drivers (with location)
                  </p>
                </div>
              </div>
              <div>
                <button
                  onClick={refreshDriverLocations}
                  className="btn btn-outline"
                  disabled={isFetchingLocations}
                >
                  {isFetchingLocations ? "Refreshing…" : "Refresh location"}
                </button>
              </div>
            </div>

            <div
              className="rounded-lg overflow-hidden border border-gray-200"
              style={{ height: "70vh" }}
            >
              <MapContainer
                center={mapPoints[0] || [20.5937, 78.9629]}
                zoom={mapPoints.length ? 12 : 5}
                style={{ height: "100%", width: "100%" }}
                zoomControl={true}
                scrollWheelZoom={true}
              >
                <TileLayer {...getReactLeafletTileLayerProps()} />

                {mapDrivers.map((driver) => {
                  const coords = driver.location.coordinates;
                  const position = [Number(coords[1]), Number(coords[0])];
                  const vehicle = driver?.vehicleId;
                  const vehicleLabel = vehicle
                    ? `${vehicle.regNo || "Vehicle"}${vehicle.make || vehicle.model || vehicle.type ? ` - ${[vehicle.make, vehicle.model, vehicle.type].filter(Boolean).join(" ")}` : ""}`
                    : "No vehicle";
                  return (
                    <Marker
                      key={driver._id}
                      position={position}
                      icon={getDriverMarkerIcon(driver)}
                    >
                      <Popup>
                        <div className="text-sm">
                          <div className="font-semibold text-gray-900">
                            {driver.name || "Driver"}
                          </div>
                          <div className="text-gray-600">
                            {driver.phone || ""}
                          </div>
                          <div className="text-gray-700 mt-1">
                            {vehicleLabel}
                          </div>
                          <div className="mt-2 flex gap-3">
                            <Link
                              to={`/drivers/${driver._id}`}
                              className="text-primary-600 hover:underline"
                            >
                              Details
                            </Link>
                            <Link
                              to={`/drivers/${driver._id}/location`}
                              className="text-primary-600 hover:underline"
                            >
                              Track
                            </Link>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}

                <FitBounds points={mapPoints} />
              </MapContainer>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowMapModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Drivers;
